/**
 * Google Apps Script Form Submission Helper
 * Submits form data to Google Apps Script Web App
 */

/**
 * Submit form data to Google Apps Script
 * @param {string} formType - Type of form (e.g., 'contact_form', 'newsletter_subscription', 'catalog_request')
 * @param {Object} formData - Form data to submit
 * @returns {Promise<Object>} Response from Apps Script
 */
export const submitToAppsScript = async (formType, formData) => {
    // Get Apps Script URL from environment variable
    const APPS_SCRIPT_URL = process.env.REACT_APP_GOOGLE_APPS_SCRIPT_URL || "https://script.google.com/macros/s/AKfycbya2Oq9ZpGGX8djRY4P0yZ-5XQwCTx03AT_xKhBBpypbM0mwOChlMuEqtTTH2wOx3e7/exec";
    
    if (!APPS_SCRIPT_URL) {
        console.warn('Google Apps Script URL not configured. Skipping Apps Script submission.');
        return { success: false, error: 'Apps Script URL not configured' };
    }

    try {
        // Prepare payload for Apps Script
        const payload = {
            formType: formType,
            timestamp: new Date().toISOString(),
            ...formData
        };

        console.log('🔵 Submitting to Apps Script:', APPS_SCRIPT_URL);
        console.log('🔵 Payload:', JSON.stringify(payload, null, 2));

        // Submit to Google Apps Script using no-cors mode
        // Google Apps Script Web Apps don't support CORS preflight requests
        // Using no-cors mode allows the request to go through, but we can't read the response
        // Using FormData format works better with no-cors mode
        const formDataToSend = new FormData();
        formDataToSend.append('data', JSON.stringify(payload));

        // Alternative: Send as URL-encoded form data (more compatible)
        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append('data', JSON.stringify(payload));

        const response = await fetch(APPS_SCRIPT_URL, {
            method: 'POST',
            mode: 'no-cors', // Critical: bypasses CORS restrictions
            // No custom headers allowed in no-cors mode
            body: urlEncodedData // Using URLSearchParams works with no-cors
        });

        // With no-cors mode, we can't read the response, but the request is sent
        // Apps Script will process it and save to Sheets
        console.log('✅ Apps Script request sent (no-cors mode)');
        console.log('✅ Data should be saved to Google Sheet - check the sheet to verify');
        console.log('ℹ️ Note: Cannot verify response due to no-cors mode, but request was sent successfully');
        
        // Return success since request was sent (even though we can't verify response)
        return { success: true, data: { message: 'Request sent - check Google Sheet to verify data was saved' } };

    } catch (error) {
        console.error('❌ Error submitting to Apps Script:', error);
        console.error('❌ Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
        });
        // Don't throw error - Apps Script is secondary to FormSubmit
        return { success: false, error: error.message };
    }
};

/**
 * Submit contact form data to Apps Script
 * @param {Object} formData - Contact form data
 */
export const submitContactFormToAppsScript = async (formData) => {
    const appsScriptData = {
        department: formData.department,
        zone: formData.zone,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        company: formData.company,
        message: formData.message,
        hasAttachment: formData.file ? 'yes' : 'no',
        attachmentName: formData.file ? formData.file.name : '',
        attachmentSize: formData.file ? formData.file.size : 0,
        privacyAccepted: formData.privacyAccepted ? 'Yes' : 'No'
    };

    return await submitToAppsScript('contact_form', appsScriptData);
};

/**
 * Submit newsletter subscription to Apps Script
 * @param {Object} formData - Newsletter form data
 */
export const submitNewsletterToAppsScript = async (formData) => {
    const appsScriptData = {
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.email,
        privacyAccepted: formData.accept ? 'Yes' : 'No',
        subscriptionDate: new Date().toLocaleString()
    };

    return await submitToAppsScript('newsletter_subscription', appsScriptData);
};

/**
 * Submit catalog request to Apps Script
 * @param {Object} formData - Catalog request form data
 */
export const submitCatalogRequestToAppsScript = async (formData) => {
    const appsScriptData = {
        email: formData.email,
        requestType: 'Technical Catalog',
        source: 'Exit Intent Popup',
        requestDate: new Date().toLocaleString(),
        privacyAccepted: 'Yes'
    };

    return await submitToAppsScript('catalog_request', appsScriptData);
};
